/**
 * src/charts/bcr-v2/api.ts
 * BarChartRace用のAPIクライアント
 * バックエンド (guts-api) と通信し、Protobufデータを取得・デコードします。
 */

// 生成済みのProtobuf定義をインポート
import { DataSet } from '../../js/generated/bcr_data_id.pb.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const BCR_API_BASE = `${API_BASE_URL}/api/bcr`;

// --- 新設: /config API のレスポンス型 ---
export interface ChartConfigResponse {
  title: string;
  description: string;
  initial_categories: string[];
  default_language: string;
  warnings: string[];
}

// --- 改修: /generate API へのリクエストパラメータ型 ---
export interface BcrApiParams {
  datasetId: string;
  configKey: string; // [追加] バックエンドで必須化された設定キー
  lang?: string;     // [追加] 要求言語 (例: 'ja', 'en')
  filterCategories?: string[];
  framesPerMonth?: number;
  startDate?: string; // YYYY-MM
  endDate?: string;   // YYYY-MM
}

export interface BcrMappingData {
  [id: number]: {
    id: number;
    name: string;
    fullName: string;
    platforms: string;
    releaseDate: string;
    category: string;
    [key: string]: any;
  };
}

// --- 改修: /generate API のレスポンス型 ---
export interface BcrDataResult {
  decodedData: DataSet;
  mappingData: BcrMappingData;
  warnings: string[]; // [追加] バックエンドからの警告リスト
}

/**
 * /daterange APIのレスポンスの型定義
 */
export interface DateRangeResponse {
  start_date: string; // YYYY-MM
  end_date: string;   // YYYY-MM
}

// ============================================================================
// API Fetchers
// ============================================================================

/**
 * [新設] 指定されたデータセットと設定キーに紐づくグラフ設定(タイトル等)を取得する
 */
export async function fetchChartConfig(datasetId: string, configKey: string, lang?: string): Promise<ChartConfigResponse> {
  const params = new URLSearchParams();
  if (lang) {
    params.append('lang', lang);
  }
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const endpoint = `${BCR_API_BASE}/config/${datasetId}/${configKey}${queryString}`;

  try {
    const response = await fetch(endpoint);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API Error ${response.status}: ${errorData.detail || response.statusText}`);
    }
    const config: ChartConfigResponse = await response.json();
    return config;
  } catch (error) {
    console.error(`[API] Failed to fetch config for dataset "${datasetId}", key "${configKey}":`, error);
    throw error;
  }
}

/**
 * 指定されたカテゴリ群のデータが存在する期間範囲を取得する
 * @param datasetId データセットID
 * @param categories フィルタリングするカテゴリの配列
 * @returns 期間範囲オブジェクト Promise
 */
export async function fetchDateRange(datasetId: string, categories: string[]): Promise<DateRangeResponse> {
  const params = new URLSearchParams();
  categories.forEach(cat => params.append('categories', cat));

  const endpoint = `${BCR_API_BASE}/daterange/${datasetId}?${params.toString()}`;

  try {
    const response = await fetch(endpoint);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API Error ${response.status}: ${errorData.detail || response.statusText}`);
    }
    const dateRange: DateRangeResponse = await response.json();
    return dateRange;
  } catch (error) {
    console.error(`[API] Failed to fetch date range for dataset "${datasetId}":`, error);
    throw error;
  }
}

/**
 * 指定されたデータセットに含まれるカテゴリ一覧を取得する
 */
export async function fetchCategories(datasetId: string): Promise<string[]> {
  const endpoint = `${BCR_API_BASE}/categories/${datasetId}`;
  try {
    const response = await fetch(endpoint);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API Error ${response.status}: ${errorData.detail || response.statusText}`);
    }
    const categories: string[] = await response.json();
    return categories;
  } catch (error) {
    console.error(`[API] Failed to fetch categories for dataset "${datasetId}":`, error);
    throw error;
  }
}

/**
 * BarChartRaceデータを取得・デコードする
 */
export async function fetchBarChartRaceData(params: BcrApiParams): Promise<BcrDataResult> {
  // [改修] リクエストボディに config_key と lang を追加
  const requestBody = {
    dataset_id: params.datasetId,
    config_key: params.configKey,
    lang: params.lang || null,
    filter_categories: params.filterCategories || null,
    frames_per_month: params.framesPerMonth || 1,
    start_date: params.startDate || null,
    end_date: params.endDate || null,
  };

  try {
    const endpoint = `${BCR_API_BASE}/generate`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API Error ${response.status}: ${errorData.detail || response.statusText}`);
    }

    const jsonResponse = await response.json();

    if (typeof jsonResponse.protobuf_base64 !== 'string' || typeof jsonResponse.mapping !== 'object' || jsonResponse.mapping === null) {
      throw new Error('Invalid API Response: Missing or invalid type for protobuf_base64 or mapping data');
    }

    const binaryString = atob(jsonResponse.protobuf_base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const decodedMessage = DataSet.decode(bytes);

    return {
      decodedData: decodedMessage,
      mappingData: jsonResponse.mapping as BcrMappingData,
      warnings: jsonResponse.warnings || [] // [追加] 警告リストの返却
    };

  } catch (error) {
    console.error('[API] Failed to fetch/decode BarChartRace data:', error);
    throw error;
  }
}